/* One unit per order for products flagged custom.restricted_sale.
 *
 * The theme's own quantity-input snippet now carries max="1" for these
 * products, but that snippet does not render the cart: this store's cart and
 * slide-out drawer come from the YourToken app (`.yt-cart-quantity-input`),
 * which the theme cannot reach. So the cap is enforced against the Cart AJAX
 * API instead, which every cart UI on the page has to go through.
 *
 * Two layers:
 *   1. requests to /cart/add.js and /cart/change.js are clamped on the way out,
 *      so a quantity above one never reaches Shopify;
 *   2. the cart is re-checked after any cart request settles, which catches
 *      anything that got through by another route.
 *
 * Which products are restricted is never hard-coded. snippets/js-variables
 * publishes the variant ids it can see from Liquid — the current product, and
 * anything already in the cart — and this file remembers them for the session,
 * so once a restricted product has been seen it stays capped.
 *
 * This is front-end enforcement. It stops every normal path through the shop,
 * but it cannot stop a crafted request: that needs a checkout validation
 * function, which needs a custom app. See claude/uv-shield-restricted-sale.md.
 */
(() => {
  const KEY = 'oshea:single-unit';
  const LIMIT = 1;

  const load = () => {
    try {
      return new Set(JSON.parse(sessionStorage.getItem(KEY) || '[]'));
    } catch (e) {
      return new Set();
    }
  };
  const save = (set) => {
    try {
      sessionStorage.setItem(KEY, JSON.stringify([...set]));
    } catch (e) {
      /* private mode — the page-rendered ids still work for this page */
    }
  };

  const ids = load();
  (window.OsheaSingleUnit || []).forEach((id) => ids.add(String(id)));
  save(ids);

  const restricted = (variantId) => ids.has(String(variantId));

  /* -------- 1. clamp outgoing requests ------------------------------- */
  const clampBody = (body) => {
    if (typeof body !== 'string') return body;
    let data;
    try {
      data = JSON.parse(body);
    } catch (e) {
      return body;                       // form-encoded: left to the sweep
    }
    let touched = false;
    if (Array.isArray(data.items)) {
      data.items.forEach((it) => {
        if (restricted(it.id) && it.quantity > LIMIT) {
          it.quantity = LIMIT;
          touched = true;
        }
      });
    }
    if (data.id && restricted(data.id) && data.quantity > LIMIT) {
      data.quantity = LIMIT;
      touched = true;
    }
    return touched ? JSON.stringify(data) : body;
  };

  const isCartWrite = (url) =>
    typeof url === 'string' && /\/cart\/(add|change|update)(\.js)?/.test(url);

  const origFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input && input.url;
    if (isCartWrite(url) && init && init.body) {
      init = Object.assign({}, init, { body: clampBody(init.body) });
    }
    const p = origFetch.call(this, input, init);
    if (isCartWrite(url)) p.then(sweepSoon, sweepSoon);
    return p;
  };

  const origSend = XMLHttpRequest.prototype.send;
  const origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__osheaCart = isCartWrite(url);
    return origOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    if (this.__osheaCart) {
      if (body) arguments[0] = clampBody(body);
      this.addEventListener('loadend', sweepSoon);
    }
    return origSend.apply(this, arguments);
  };

  /* -------- 2. sweep the cart afterwards ----------------------------- */
  let pending = null;
  function sweepSoon() {
    clearTimeout(pending);
    pending = setTimeout(sweep, 350);
  }

  let sweeping = false;
  async function sweep() {
    if (sweeping || !ids.size) return;
    sweeping = true;
    try {
      const cart = await origFetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const over = (cart.items || []).filter((i) => restricted(i.variant_id) && i.quantity > LIMIT);
      for (const line of over) {
        await origFetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: String(line.key), quantity: LIMIT }),
        });
      }
      if (over.length) {
        notify();
        // Let the cart UI redraw. The YourToken drawer and the theme both
        // listen for their own events, so a reload is the only thing that is
        // guaranteed to agree with the server on every surface.
        if (document.body.classList.contains('template-cart') || location.pathname.endsWith('/cart')) {
          location.reload();
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
      }
    } catch (e) {
      /* never let a cart guard break the page */
    } finally {
      sweeping = false;
    }
  }

  let toast = null;
  function notify() {
    if (toast) return;
    toast = document.createElement('div');
    toast.textContent = 'Limited to 1 per order.';
    toast.setAttribute('role', 'status');
    toast.style.cssText =
      'position:fixed;left:50%;bottom:2rem;transform:translateX(-50%);z-index:2147483000;' +
      'background:#5b1310;color:#fff;padding:1rem 1.6rem;border-radius:999px;' +
      'font:600 1.3rem/1.2 inherit;box-shadow:0 4px 16px rgba(0,0,0,.25)';
    document.body.appendChild(toast);
    setTimeout(() => {
      toast && toast.remove();
      toast = null;
    }, 3200);
  }

  if (ids.size) sweepSoon();
})();
